(() => {
  'use strict';

  const root = document.getElementById('MorrugoCurrency');
  const select = document.getElementById('MorrugoCurrencySelect');
  if (!root || !select) return;

  const SHOP_CURRENCY = 'MAD';
  const STORAGE_CURRENCY = 'morrugo_display_currency';
  const STORAGE_COUNTRY = 'morrugo_geo_country';
  const STORAGE_COUNTRY_TIME = 'morrugo_geo_country_time';
  const STORAGE_RATES = 'morrugo_fx_rates_mad';
  const STORAGE_RATES_TIME = 'morrugo_fx_rates_mad_time';
  const CACHE_MS = 12 * 60 * 60 * 1000;

  const euroCountries = new Set([
    'AD','AT','BE','HR','CY','EE','FI','FR','DE','GR','IE','IT','LV','LT','LU','MT','MC','ME','NL','PT','SM','SK','SI','ES','VA','XK'
  ]);

  const currencyByCountry = {
    MA: 'MAD', US: 'USD', PR: 'USD', VI: 'USD', GU: 'USD', AS: 'USD', MP: 'USD',
    GB: 'GBP', CA: 'CAD', AU: 'AUD', CH: 'CHF', LI: 'CHF', NZ: 'NZD',
    SE: 'SEK', NO: 'NOK', DK: 'DKK', PL: 'PLN', CZ: 'CZK', HU: 'HUF', RO: 'RON',
    AE: 'AED', SA: 'SAR', QA: 'QAR', KW: 'KWD', BH: 'BHD', OM: 'OMR',
    TR: 'TRY', JP: 'JPY', CN: 'CNY', HK: 'HKD', KR: 'KRW', IN: 'INR', SG: 'SGD',
    MX: 'MXN', BR: 'BRL', ZA: 'ZAR'
  };

  const originals = new WeakMap();
  const rendered = new WeakMap();
  let activeCurrency = SHOP_CURRENCY;
  let rates = null;
  let applying = false;
  let scheduled = false;

  const priceRegex = /(\d[\d\s\u00a0\u202f,.]*\d|\d)\s*(?:dh|MAD)\b/gi;

  function safeGet(key) {
    try { return localStorage.getItem(key); } catch (_) { return null; }
  }

  function safeSet(key, value) {
    try { localStorage.setItem(key, value); } catch (_) {}
  }

  function countryToCurrency(country) {
    const code = String(country || '').toUpperCase();
    if (!code) return null;
    if (euroCountries.has(code)) return 'EUR';
    return currencyByCountry[code] || 'USD';
  }

  function browserCountry() {
    const candidates = [navigator.language].concat(navigator.languages || []);
    for (const locale of candidates) {
      const match = String(locale || '').match(/[-_]([A-Za-z]{2})$/);
      if (match) return match[1].toUpperCase();
    }
    return null;
  }

  async function detectCountry() {
    const cached = safeGet(STORAGE_COUNTRY);
    const cachedAt = Number(safeGet(STORAGE_COUNTRY_TIME) || 0);
    if (cached && Date.now() - cachedAt < CACHE_MS) return cached;

    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 2500);
      const response = await fetch('https://api.country.is/', {
        signal: controller.signal,
        cache: 'no-store',
        credentials: 'omit'
      });
      clearTimeout(timer);
      if (response.ok) {
        const data = await response.json();
        const code = String(data.country || '').toUpperCase();
        if (/^[A-Z]{2}$/.test(code)) {
          safeSet(STORAGE_COUNTRY, code);
          safeSet(STORAGE_COUNTRY_TIME, String(Date.now()));
          return code;
        }
      }
    } catch (_) {}

    return browserCountry() || String(root.dataset.country || (window.Shopify && window.Shopify.country) || 'MA').toUpperCase();
  }

  async function getRates() {
    if (rates) return rates;

    const cachedAt = Number(safeGet(STORAGE_RATES_TIME) || 0);
    const cachedRaw = safeGet(STORAGE_RATES);
    if (cachedRaw && Date.now() - cachedAt < CACHE_MS) {
      try {
        const parsed = JSON.parse(cachedRaw);
        if (parsed && parsed.MAD === 1) {
          rates = parsed;
          return rates;
        }
      } catch (_) {}
    }

    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 3500);
      const response = await fetch('https://open.er-api.com/v6/latest/MAD', {
        signal: controller.signal,
        cache: 'no-store',
        credentials: 'omit'
      });
      clearTimeout(timer);
      if (response.ok) {
        const data = await response.json();
        if (data && data.rates && data.rates.MAD) {
          rates = data.rates;
          safeSet(STORAGE_RATES, JSON.stringify(rates));
          safeSet(STORAGE_RATES_TIME, String(Date.now()));
          return rates;
        }
      }
    } catch (_) {}

    return null;
  }

  function parseMadAmount(raw) {
    let value = String(raw).replace(/[\s\u00a0\u202f]/g, '');
    const comma = value.lastIndexOf(',');
    const dot = value.lastIndexOf('.');

    if (comma !== -1 && dot !== -1) {
      if (dot > comma) value = value.replace(/,/g, '');
      else value = value.replace(/\./g, '').replace(',', '.');
    } else if (comma !== -1) {
      const decimals = value.length - comma - 1;
      value = decimals === 1 || decimals === 2 ? value.replace(',', '.') : value.replace(/,/g, '');
    } else if (dot !== -1) {
      const decimals = value.length - dot - 1;
      if (!(decimals === 1 || decimals === 2)) value = value.replace(/\./g, '');
    }

    const number = Number(value);
    return Number.isFinite(number) ? number : null;
  }

  function ensureCurrencyOption(currency) {
    if (!currency || select.querySelector(`option[value="${currency}"]`)) return;
    const option = document.createElement('option');
    option.value = currency;
    option.textContent = currency;
    select.appendChild(option);
  }

  function formatMoney(amount, currency) {
    const locale = root.dataset.locale || document.documentElement.lang || 'en';
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }).format(amount);
    } catch (_) {
      return `${amount.toFixed(2)} ${currency}`;
    }
  }

  function shouldSkip(node) {
    const parent = node.parentElement;
    if (!parent) return true;
    return Boolean(parent.closest('script, style, noscript, textarea, select, option, code, pre, #MorrugoCurrency'));
  }

  function hasMadPrice(text) {
    priceRegex.lastIndex = 0;
    const found = priceRegex.test(text);
    priceRegex.lastIndex = 0;
    return found;
  }

  function convertTextNode(node) {
    if (!node || node.nodeType !== Node.TEXT_NODE || shouldSkip(node)) return;

    const current = node.nodeValue || '';
    const lastRendered = rendered.get(node);

    if (lastRendered !== undefined && current !== lastRendered && hasMadPrice(current)) {
      originals.set(node, current);
    }

    if (!originals.has(node)) {
      if (!hasMadPrice(current)) return;
      originals.set(node, current);
    }

    const source = originals.get(node);
    let next = source;

    if (activeCurrency !== SHOP_CURRENCY && rates && rates[activeCurrency]) {
      const fx = Number(rates[activeCurrency]);
      priceRegex.lastIndex = 0;
      next = source.replace(priceRegex, (full, amountRaw) => {
        const amount = parseMadAmount(amountRaw);
        return amount === null ? full : formatMoney(amount * fx, activeCurrency);
      });
      priceRegex.lastIndex = 0;
    }

    rendered.set(node, next);
    if (node.nodeValue !== next) node.nodeValue = next;
  }

  function scan(rootNode = document.body) {
    if (!rootNode) return;
    const walker = document.createTreeWalker(rootNode, NodeFilter.SHOW_TEXT);
    let node;
    while ((node = walker.nextNode())) convertTextNode(node);
  }

  async function applyCurrency(currency) {
    activeCurrency = currency || SHOP_CURRENCY;
    ensureCurrencyOption(activeCurrency);
    select.value = activeCurrency;

    if (activeCurrency !== SHOP_CURRENCY) {
      const loaded = await getRates();
      if (!loaded || !loaded[activeCurrency]) {
        activeCurrency = SHOP_CURRENCY;
        select.value = SHOP_CURRENCY;
      }
    }

    applying = true;
    scan(document.body);
    applying = false;
    document.documentElement.dataset.displayCurrency = activeCurrency;
  }

  function scheduleScan() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      if (!applying) scan(document.body);
    });
  }

  const observer = new MutationObserver((mutations) => {
    if (applying) return;
    for (const mutation of mutations) {
      if (mutation.type === 'characterData' || mutation.addedNodes.length) {
        scheduleScan();
        break;
      }
    }
  });

  select.addEventListener('change', async () => {
    safeSet(STORAGE_CURRENCY, select.value);
    await applyCurrency(select.value);
  });

  async function init() {
    const manual = safeGet(STORAGE_CURRENCY);
    let currency = manual;

    if (!currency) {
      const country = await detectCountry();
      currency = countryToCurrency(country) || SHOP_CURRENCY;
    }

    await applyCurrency(currency);
    observer.observe(document.body, { subtree: true, childList: true, characterData: true });
  }

  init();
})();
